import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FormSection } from '@strata/react-form-section';
import { DetailLabel, DetailValue } from '@strata/react-label-value-pairs';
import {
    ACCEPTED_QUESTION_ID,
    ComponentTypes,
    decode,
    formatQuestionLabel,
} from '@strata/react-question-tree';
import { Icon } from '@strata/react-status-icons';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { QuestionDeleteDialog } from 'pages/clinicalPolicy/dialogs/QuestionDeleteDialog';
import { QuestionDialog } from 'pages/clinicalPolicy/dialogs/QuestionDialog';
import { handleError } from 'services/ErrorManagement';

const renumberQuestions = (questions) => {
    const questionIdChangeMap = { [ACCEPTED_QUESTION_ID]: ACCEPTED_QUESTION_ID };
    return (
        questions
            // Update question ids based on array index
            .map((question, index) => {
                const newId = index + 1;
                questionIdChangeMap[question.question_id] = newId;
                return { ...question, question_id: newId };
            })
            // Update option_validation based on new question ids
            .map((question) => ({
                ...question,
                option_validation: question.option_validation.map((rule) => ({
                    ...rule,
                    question_id: questionIdChangeMap[rule.question_id],
                })),
            }))
    );
};

export const QuestionsTab = ({ questions = undefined, updateQuestions, readOnly, payerId }) => {
    const [selectedQuestion, setSelectedQuestion] = useState();
    const [questionToEdit, setQuestionToEdit] = useState();
    const [questionToDelete, setQuestionToDelete] = useState();
    return (
        <div className="sds_policy-edit">
            <QuestionDialog
                question={questionToEdit}
                existingQuestionList={questions}
                payerId={payerId}
                showAutoAccept={questionToEdit?.starting_point && questions?.length <= 1}
                isOpen={!!questionToEdit}
                onSubmit={async (updatedQuestion) => {
                    const existingQuestionIndex = questions.findIndex(
                        (question) => question.question_id === updatedQuestion.question_id,
                    );
                    const nodes = renumberQuestions(
                        existingQuestionIndex === -1
                            ? [...questions, updatedQuestion]
                            : questions.map((question, index) =>
                                  index === existingQuestionIndex ? updatedQuestion : question,
                              ),
                    );
                    await updateQuestions(nodes);
                    setSelectedQuestion(updatedQuestion);
                }}
                onSecondaryButtonClick={() => setQuestionToEdit()}
            />
            <QuestionDeleteDialog
                question={questionToDelete}
                isOpen={!!questionToDelete}
                onPrimaryButtonClick={async () => {
                    try {
                        const nodes = renumberQuestions(
                            questions
                                .filter(
                                    (question) =>
                                        question.question_id !== questionToDelete?.question_id,
                                )
                                .map((question) => ({
                                    ...question,
                                    option_validation: question.option_validation.map((rule) =>
                                        rule.question_id === questionToDelete?.question_id
                                            ? {
                                                  ...rule,
                                                  question_id: undefined,
                                              }
                                            : rule,
                                    ),
                                })),
                        );
                        await updateQuestions(nodes);
                        setQuestionToDelete();
                        setSelectedQuestion();
                    } catch (err) {
                        handleError('Error deleting question.', err);
                    }
                }}
                onSecondaryButtonClick={() => setQuestionToDelete()}
            />
            <div className="sds_policy-edit__column" style={{ width: '45%' }}>
                <FormSection
                    sectionTitle="Question Order"
                    isLoading={!questions}
                    rightHeaderContent={
                        !readOnly &&
                        questions?.every(
                            (question) => question.component !== ComponentTypes.AUTO_ACCEPT,
                        ) && (
                            <ActionButtonText
                                leftIcon={IconNames.ADD}
                                label="Add Question"
                                onClick={() =>
                                    setQuestionToEdit({
                                        question_id:
                                            questions.reduce(
                                                (a, b) => Math.max(a, b.question_id),
                                                0,
                                            ) + 1,
                                        starting_point: questions.length === 0,
                                    })
                                }
                            />
                        )
                    }
                    content={questions?.map((question) => (
                        <div
                            key={question.question_id}
                            className="sds_policy-edit__question padding__around--medium margin__bottom--medium"
                            onClick={() => setSelectedQuestion(question)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSelectedQuestion(question);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                        >
                            <span className="sds_policy-edit__question--text">
                                {formatQuestionLabel(
                                    `${question.question_id}. ${question.question_text}`,
                                )}
                            </span>
                            {!readOnly && (
                                <>
                                    <Icon
                                        className="sds_policy-edit__question--icon"
                                        title="Edit"
                                        icon={IconNames.EDIT}
                                        onClick={() => setQuestionToEdit(question)}
                                    />
                                    {!question.starting_point && (
                                        <Icon
                                            className="sds_policy-edit__question--icon padding__left--small"
                                            title="Delete"
                                            icon={IconNames.TRASH}
                                            onClick={() => setQuestionToDelete(question)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                />
            </div>
            <div className="sds_policy-edit__column" style={{ width: '55%' }}>
                <FormSection
                    sectionTitle="Preview"
                    isLoading={!questions}
                    content={
                        selectedQuestion ? (
                            <>
                                <Formik initialValues={{}}>
                                    {(formik) => decode({ question: selectedQuestion, formik })}
                                </Formik>
                                {selectedQuestion.benefit_auto_populate?.length > 0 && (
                                    <div className="padding__top--medium">
                                        <DetailLabel label="Benefit Attribute Auto Population" />
                                        {selectedQuestion.benefit_auto_populate.map((condition) => (
                                            <div
                                                key={`${condition.attribute_type}${condition.attribute_value}${condition.option_id}`}
                                                className="sds_policy-edit__preview-pane__benefit-attributes"
                                            >
                                                <div className="sds_policy-edit__preview-pane__benefit-attributes--condition-text">
                                                    If
                                                </div>
                                                <DetailValue value={condition.attribute_type} />
                                                <div className="sds_policy-edit__preview-pane__benefit-attributes--condition-text">
                                                    Is
                                                </div>
                                                <DetailValue value={condition.attribute_value} />
                                                <div className="sds_policy-edit__preview-pane__benefit-attributes--condition-text">
                                                    Then
                                                </div>
                                                <DetailValue
                                                    value={
                                                        selectedQuestion.options?.find(
                                                            (option) =>
                                                                option.option_id ===
                                                                condition.option_id,
                                                        )?.answer
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="sds_policy-edit__preview-pane__default-message font-bold--uppercase">
                                Click on a question to the left to preview here
                            </div>
                        )
                    }
                />
            </div>
        </div>
    );
};

QuestionsTab.propTypes = {
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    updateQuestions: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    payerId: PropTypes.string.isRequired,
};
